import React from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";
import {
  Activity,
  AlertTriangle,
  ShieldAlert,
  Clock,
  TrendingUp,
  Wrench,
  Gauge,
  Zap,
  IndianRupee,
  Filter,
  Download,
  ChevronRight,
  HeartPulse,
  Search,
} from "lucide-react";
import { useSelector } from "react-redux";

// --- Asset Health Analytics Mock Data ---
const HEALTH_DATA = {
  kpis: {
    highRiskAssets: "03",
    avgVibration: "4.2 mm/s",
    tripsThisMonth: "12",
    totalMaintCost: "₹ 85.2k",
    statusText: "Requires immediate attention",
  },
  failureRiskRanking: [
    {
      asset: "Pump-07",
      riskScore: 88,
      health: "Critical",
      prediction: "Failure in 4 days",
    },
    {
      asset: "Motor-B2",
      riskScore: 65,
      health: "Warning",
      prediction: "Failure in 12 days",
    },
    {
      asset: "Flowmeter-S",
      riskScore: 42,
      health: "Moderate",
      prediction: "Failure in 25 days",
    },
    {
      asset: "Pump-01",
      riskScore: 12,
      health: "Healthy",
      prediction: "No risk detected",
    },
  ],
  downtimeHours: [
    { asset: "P-07", hours: 45 },
    { asset: "M-B2", hours: 22 },
    { asset: "F-S", hours: 10 },
    { asset: "P-01", hours: 5 },
    { asset: "V-04", hours: 18 },
  ],
  maintCostDistribution: [
    { name: "Predictive", value: 45000, color: "#3b82f6" },
    { name: "Reactive", value: 30000, color: "#ef4444" },
    { name: "Routine", value: 25000, color: "#10b981" },
  ],
  costByAsset: [
    { asset: "Pump-07", cost: 35000 },
    { asset: "Motor-B2", cost: 15000 },
    { asset: "Flowmeter", cost: 8000 },
    { asset: "Valve-A", cost: 4000 },
  ],
};

const COLORS = ["#3b82f6", "#ef4444", "#10b981", "#f59e0b"];

const AssetHealth = () => {
  const themeMode = useSelector((state) => state.theme?.mode);
  const cardBg = themeMode ? "bg-slate-900/90" : "bg-white";
  const textColor = themeMode ? "dark_text-color" : "text-primary";
  const bgColor = themeMode ? "bg_light_main" : "bg-white";
  const borderCol = themeMode ? "border_them_2" : "border-primary";

  return (
    <div className={`w-full ${bgColor} ${textColor} h-full`}>
      <div
        className={`py-4 md:px-12 px-6 border-b ${borderCol} flex justify-between items-center h-[57px]`}
      >
        <div
          className={`flex ps-3 md:ps-0 text-sm md:text-base ${textColor} items-center  gap-x-2`}
        >
          <p>
            <HeartPulse size={16} />
          </p>
          <p className="font-semibold">Asset Health & Reliability</p>
        </div>
      </div>

      <div className=" h-[calc(100vh-50px)] pb-10 pt-5 sidebar_scroll overflow-y-auto sidebar_scroll">
        <div className="px-3 md:px-5 lg:px-12">
          {/* --- KPI Grid (As per image_bf031a.png) --- */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <HealthKPICard
              label="High Risk Assets"
              value={HEALTH_DATA.kpis.highRiskAssets}
              sub={HEALTH_DATA.kpis.statusText}
              icon={<ShieldAlert size={22} />}
              color="text-red-500"
              indicator="bg-red-500"
              cardBg={cardBg}
              borderCol={borderCol}
              isUrgent
            />
            <HealthKPICard
              label="Avg Vibration"
              value={HEALTH_DATA.kpis.avgVibration}
              sub="Within safe limits"
              icon={<Activity size={22} />}
              color="text-green-500"
              indicator="bg-green-500"
              cardBg={cardBg}
              borderCol={borderCol}
            />
            <HealthKPICard
              label="Trips This Month"
              value={HEALTH_DATA.kpis.tripsThisMonth}
              sub="High frequency in Zone B"
              icon={<Clock size={22} />}
              color="text-yellow-500"
              indicator="bg-yellow-500"
              cardBg={cardBg}
              borderCol={borderCol}
            />
            <HealthKPICard
              label="Total Maint. Cost"
              value={HEALTH_DATA.kpis.totalMaintCost}
              sub="Predictive vs Reactive"
              icon={<IndianRupee size={22} />}
              color="text-purple-500"
              indicator="bg-purple-500"
              cardBg={cardBg}
              borderCol={borderCol}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* --- Report: Failure Risk Ranking Table --- */}
            <div
              className={`lg:col-span-7 ${cardBg} border ${borderCol} rounded-3xl overflow-hidden shadow-sm`}
            >
              <div className="p-3 border-b flex items-center justify-between">
                <h3 className="font-semibold text-base flex items-center gap-2 underline decoration-red-500 underline-offset-8">
                  <AlertTriangle size={20} className="text-red-500" /> Failure
                  Risk Ranking
                </h3>
                <span className="text-[10px] font-black opacity-40 uppercase tracking-widest">
                  Predictive Model v2.4
                </span>
              </div>
              <div className="p-0 overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="text-[10px] uppercase opacity-40 font-black border-b border-slate-500/10">
                    <tr>
                      <th className="p-4">Asset Identification</th>
                      <th className="p-4 text-center">Risk Score</th>
                      <th className="p-4">Health Status</th>
                      <th className="p-4 text-right">Prediction Insight</th>
                    </tr>
                  </thead>
                  <tbody className="text-xs">
                    {HEALTH_DATA.failureRiskRanking.map((row, i) => (
                      <tr
                        key={i}
                        className="border-b border-slate-500/5 hover:bg-slate-500/5 transition-colors group"
                      >
                        <td className="p-4 font-semibold">{row.asset}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 justify-center">
                            <div className="w-16 bg-slate-200 h-1.5 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${
                                  row.riskScore > 70
                                    ? "bg-red-500"
                                    : "bg-green-500"
                                }`}
                                style={{ width: `${row.riskScore}%` }}
                              ></div>
                            </div>
                            <span className="font-bold">{row.riskScore}%</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <span
                            className={`px-2 py-1 rounded-lg font-semibold uppercase text-[9px] ${
                              row.health === "Critical"
                                ? "bg-red-100 text-red-600"
                                : row.health === "Warning"
                                ? "bg-yellow-100 text-yellow-600"
                                : "bg-green-100 text-green-700"
                            }`}
                          >
                            {row.health}
                          </span>
                        </td>
                        <td className="p-4 text-right italic font-medium opacity-70 group-hover:opacity-100">
                          {row.prediction}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* --- Report: Maintenance Type Distribution (Pie) --- */}
            <div
              className={`lg:col-span-5 ${cardBg} border ${borderCol} rounded-3xl p-3 shadow-sm`}
            >
              <h3 className="font-semibold text-base mb-6 flex items-center gap-2">
                <TrendingUp size={20} className="text-blue-500" /> Maintenance
                Type Distribution
              </h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={HEALTH_DATA.maintCostDistribution}
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {HEALTH_DATA.maintCostDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* --- Report: Downtime by Asset (Bar Chart) --- */}
            <div
              className={`lg:col-span-6 ${cardBg} border ${borderCol} rounded-3xl p-3 shadow-sm`}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-semibold text-base tracking-tight italic">
                  Downtime by Asset (Total Hours)
                </h3>
                <Clock size={20} className="opacity-20" />
              </div>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={HEALTH_DATA.downtimeHours}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      strokeOpacity={0.1}
                    />
                    <XAxis
                      dataKey="asset"
                      fontSize={12}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      fontSize={12}
                      axisLine={false}
                      tickLine={false}
                      label={{
                        value: "Hours",
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <Tooltip
                      cursor={{ fill: "transparent" }}
                      contentStyle={{ borderRadius: "12px" }}
                    />
                    <Bar
                      dataKey="hours"
                      fill="#3b82f6"
                      radius={[6, 6, 0, 0]}
                      barSize={35}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* --- Report: Maintenance Cost by Asset --- */}
            <div
              className={`lg:col-span-6 ${cardBg} border ${borderCol} rounded-3xl p-3 shadow-sm`}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-semibold text-base tracking-tight italic">
                  Maintenance Cost Breakdown (₹)
                </h3>
                <IndianRupee size={20} className="opacity-20" />
              </div>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={HEALTH_DATA.costByAsset} layout="vertical">
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={false}
                      strokeOpacity={0.1}
                    />
                    <XAxis
                      type="number"
                      fontSize={10}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      dataKey="asset"
                      type="category"
                      fontSize={12}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip cursor={{ fill: "transparent" }} />
                    <Bar
                      dataKey="cost"
                      fill="#8b5cf6"
                      radius={[0, 6, 6, 0]}
                      barSize={25}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* --- Strategic Action Panel --- */}
          {/* <div className="mt-8 bg-blue-600 rounded-3xl p-8 text-white flex flex-col md:flex-row justify-between items-center shadow-xl shadow-blue-500/20">
            <div className="mb-4 md:mb-0">
              <h4 className="text-xl font-black mb-1">
                Preventive Action Triggered
              </h4>
              <p className="opacity-80 font-medium">
                System suggests immediate bearing replacement for Pump-07 based
                on vibration frequency shifts.
              </p>
            </div>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:scale-105 transition-transform flex items-center gap-2">
              Create Work Order <ChevronRight size={16} />
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
};

// --- Reusable Sub-Component for Health KPIs ---
const HealthKPICard = ({
  label,
  value,
  sub,
  icon,
  color,
  indicator,
  cardBg,
  borderCol,
  isUrgent,
}) => (
  <div
    className={`${cardBg} border ${borderCol} p-6 rounded-3xl shadow-sm hover:shadow-xl transition-all relative overflow-hidden group`}
  >
    <div
      className={`absolute top-0 right-0 w-24 h-24 ${indicator} opacity-[0.03] rounded-full -mr-8 -mt-8 group-hover:scale-150 transition-transform duration-700`}
    ></div>
    <div className="flex justify-between items-start mb-4 relative z-10">
      <div className={`${color} bg-slate-500/5 p-3 rounded-2xl`}>{icon}</div>
      {isUrgent && (
        <div className="w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
      )}
    </div>
    <p className="text-xl font-black tracking-tighter mb-1 relative z-10">
      {value}
    </p>
    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1 relative z-10">
      {label}
    </p>
    <p
      className={`text-[10px] font-bold italic opacity-60 ${
        isUrgent ? "text-red-500 opacity-100" : ""
      }`}
    >
      {sub}
    </p>
  </div>
);

export default AssetHealth;
